'use client'

// SUMMARY: Pricing card grid with monthly/yearly billing toggle and stacked launch-sale discounts

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { Check, Rocket } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatPrice, PlanId, PLANS } from '@/lib/stripe/config'
import { applyDiscount, LAUNCH_SALE, YEARLY_DISCOUNT_PERCENT } from '@/lib/launch-sale'

// ===============================
// SECTION: PRICE BREAKDOWN
// ===============================

interface PlanPricing {
	/** Regular monthly price before any discount */
	original: number
	/** Price after the yearly discount (= original when billing monthly) — what's paid once the promo ends */
	base: number
	/** Price during the launch-promo months (= base when the sale is off) */
	current: number
}

function planPricing(priceCents: number, yearly: boolean): PlanPricing {
	const base = yearly ? applyDiscount(priceCents, YEARLY_DISCOUNT_PERCENT) : priceCents
	// Launch promo stacks on top of either interval (yearly already carries the standing yearly discount).
	const current = LAUNCH_SALE.active ? applyDiscount(base, LAUNCH_SALE.discountPercent) : base
	return { original: priceCents, base, current }
}

// ===============================
// SECTION: COMPONENT
// ===============================

export function PricingCards() {
	const t = useTranslations('pricing')
	const [yearly, setYearly] = useState(false)

	return (
		<div>
			{/* Launch-sale banner */}
			{LAUNCH_SALE.active && (
				<div className="mb-8 flex justify-center">
					<div className="flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
						<Rocket className="h-4 w-4" />
						{t('launchBanner', { percent: LAUNCH_SALE.discountPercent, months: LAUNCH_SALE.months })}
					</div>
				</div>
			)}

			{/* Billing-interval toggle */}
			<div className="mb-10 flex items-center justify-center">
				<div className="flex items-center rounded-full border border-border/60 bg-background/60 p-1 backdrop-blur-sm">
					<button
						onClick={() => setYearly(false)}
						className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
							!yearly ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'
						}`}
					>
						{t('billingMonthly')}
					</button>
					<button
						onClick={() => setYearly(true)}
						className={`flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
							yearly ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'
						}`}
					>
						{t('billingYearly')}
						<span
							className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
								yearly
									? 'bg-white/20 text-white'
									: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
							}`}
						>
							−{YEARLY_DISCOUNT_PERCENT}%
						</span>
					</button>
				</div>
			</div>

			{/* Plan cards */}
			<div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 max-w-6xl mx-auto">
				{(Object.entries(PLANS) as [PlanId, (typeof PLANS)[PlanId]][]).map(([planId, plan]) => {
					const isPopular = planId === 'STANDARD'
					const isPro = planId === 'PRO'
					const pricing = planPricing(plan.price, yearly)
					const discounted = plan.price > 0 && pricing.current < pricing.original

					return (
						<div key={planId} className={`relative ${isPro ? 'group' : ''}`}>
							{isPro && (
								<>
									<div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600 opacity-75 blur-sm group-hover:opacity-100 transition-opacity" />
									<div className="animate-border-spin absolute -inset-[2px] rounded-2xl" />
								</>
							)}
							<Card
								className={`relative flex flex-col h-full ${isPopular ? 'border-blue-500 shadow-lg dark:border-blue-400' : ''} ${isPro ? 'border-transparent' : ''}`}
							>
								{(isPopular || isPro) && (
									<Badge
										className={`absolute -top-2.5 left-1/2 -translate-x-1/2 ${isPro ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white' : 'bg-blue-600 text-white'}`}
									>
										{isPopular ? t('popular') : t('plans.PRO.name')}
									</Badge>
								)}
								<CardHeader className="text-center pb-4">
									<CardTitle className="text-xl">{t(`plans.${planId}.name`)}</CardTitle>
									<div className="mt-4">
										{plan.price === 0 ? (
											<span className="text-4xl font-bold">{t('free')}</span>
										) : (
											<>
												{discounted && (
													<div className="flex items-center justify-center gap-2">
														<span className="text-sm text-muted-foreground line-through">
															{formatPrice(pricing.original, plan.currency)}
														</span>
														{LAUNCH_SALE.active && (
															<Badge className="bg-blue-600 px-1.5 py-0 text-[10px] text-white">
																−{LAUNCH_SALE.discountPercent}%
															</Badge>
														)}
													</div>
												)}
												<span className="text-4xl font-bold">{formatPrice(pricing.current, plan.currency)}</span>
												<span className="text-sm text-muted-foreground ml-1 inline-block whitespace-nowrap">
													/{t('monthly')}
												</span>
												<div className="mt-2 min-h-8 space-y-0.5 text-xs text-muted-foreground">
													{yearly && <p>{t('yearlyBilled')}</p>}
													{LAUNCH_SALE.active && (
														<p>
															{t('launchPriceNote', {
																months: LAUNCH_SALE.months,
																price: formatPrice(pricing.base, plan.currency),
															})}
														</p>
													)}
												</div>
											</>
										)}
									</div>
								</CardHeader>
								<CardContent className="flex flex-1 flex-col">
									<ul className="space-y-3 mb-8 flex-1">
										{(t.raw(`plans.${planId}.features`) as string[]).map((feature, i) => (
											<li key={i} className="flex items-start gap-2">
												<Check className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
												<span className="text-sm text-muted-foreground">{feature}</span>
											</li>
										))}
									</ul>
									<Link href="/auth/register">
										<Button
											className={`w-full ${isPro ? 'bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-800 hover:to-blue-600 text-white' : ''}`}
											variant={plan.price === 0 ? 'outline' : 'default'}
										>
											{plan.price === 0 ? t('getStarted') : t('subscribe')}
										</Button>
									</Link>
								</CardContent>
							</Card>
						</div>
					)
				})}
			</div>

			{/* PAngV / § 312j Abs. 2 BGB: a consumer-facing price must state whether VAT is included.
			    Without it the same figure is ambiguous - the AGB treat it as gross for consumers and net for
			    businesses - which is exactly the ambiguity German price-transparency rules forbid. */}
			<p className="mt-8 text-center text-xs text-muted-foreground">
				{t('vatIncluded')} {t('vatBusiness')}
			</p>
		</div>
	)
}
